import React, { useState, useEffect, useRef } from 'react';
import { X, Camera, Trash2 } from 'lucide-react';

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif';
const CATEGORIES = ['Springs', 'Rollers', 'Tracks', 'Cables & Drums', 'Openers', 'Remotes', 'Panels', 'Hardware', 'Tools', 'Other'];

const INP = {
  width: '100%', padding: '11px 14px', border: 'none', borderRadius: 10,
  fontSize: 15, outline: 'none', background: '#F2F2F7', color: '#1C1C1E',
  fontFamily: FONT, boxSizing: 'border-box',
};
const LBL = { display: 'block', fontSize: 11, fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8, fontFamily: FONT };

function compressImage(file, maxPx = 900, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('load failed')); };
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxPx || height > maxPx) {
        if (width > height) { height = Math.round(height * maxPx / width); width = maxPx; }
        else { width = Math.round(width * maxPx / height); height = maxPx; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => {
        if (blob) resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
        else reject(new Error('toBlob failed'));
      }, 'image/jpeg', quality);
    };
    img.src = url;
  });
}

export default function ItemModal({ item, suppliers, onSave, onClose }) {
  const [form, setForm] = useState({
    side: '', name: '', category: 'Other', quantity: 0, minQuantity: 0,
    costPrice: 0, sellingPrice: 0, supplier: '', location: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (item) setForm({ ...item });
  }, [item]);

  useEffect(() => {
    return () => { if (imagePreview) URL.revokeObjectURL(imagePreview); };
  }, [imagePreview]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleImageChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    const originalUrl = URL.createObjectURL(file);
    setImagePreview(originalUrl);
    setRemoveImage(false);
    try {
      const compressed = await compressImage(file);
      setImageFile(compressed);
      URL.revokeObjectURL(originalUrl);
      setImagePreview(URL.createObjectURL(compressed));
    } catch {
      setImageFile(file);
    }
  }

  function handleRemoveImage() {
    setImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setRemoveImage(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const currentImage = removeImage ? null : (imagePreview || form.imagePath || null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Product name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(form, imageFile, removeImage);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save item.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: FONT }}>
      <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 620, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.28)' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1C1C1E', margin: 0 }}>{item ? 'Edit Item' : 'Add Inventory Item'}</h2>
          <button onClick={onClose} style={{ background: '#F2F2F7', border: 'none', cursor: 'pointer', borderRadius: 50, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8E8E93' }}>
            <X size={15} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '0 24px 24px' }}>
          {error && <div style={{ background: '#FFF1F0', borderRadius: 10, padding: '10px 14px', color: '#FF3B30', fontSize: 13, marginBottom: 16 }}>{error}</div>}

          {/* Photo */}
          <div style={{ marginBottom: 20 }}>
            <label style={LBL}>Product Photo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div
                onClick={() => fileInputRef.current.click()}
                style={{
                  width: 84, height: 84, borderRadius: 14, flexShrink: 0, cursor: 'pointer',
                  border: currentImage ? 'none' : '2px dashed #C7C7CC',
                  overflow: 'hidden', background: '#F2F2F7',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {currentImage
                  ? <img src={currentImage} alt="Product" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <Camera size={26} color="#C7C7CC" />
                }
              </div>
              <div style={{ flex: 1 }}>
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} />
                <button type="button" onClick={() => fileInputRef.current.click()}
                  style={{ padding: '8px 16px', border: 'none', borderRadius: 10, background: '#F2F2F7', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#1C1C1E', display: 'block', marginBottom: 8, fontFamily: FONT }}>
                  {currentImage ? 'Replace Photo' : 'Add Photo'}
                </button>
                {imageFile && <div style={{ fontSize: 11, color: '#8E8E93', marginBottom: 6 }}>{imageFile.name}</div>}
                {currentImage && (
                  <button type="button" onClick={handleRemoveImage}
                    style={{ padding: '5px 12px', border: 'none', borderRadius: 8, background: 'rgba(255,59,48,0.08)', cursor: 'pointer', fontSize: 11, color: '#FF3B30', display: 'flex', alignItems: 'center', gap: 4, fontFamily: FONT }}>
                    <Trash2 size={10} /> Remove
                  </button>
                )}
                <div style={{ fontSize: 11, color: '#C7C7CC', marginTop: 6 }}>JPG, PNG, WEBP · max 5MB</div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={LBL}>Side</label>
              <input type="text" value={form.side} onChange={e => set('side', e.target.value)} style={INP} placeholder="e.g. Left, Right, Both" />
            </div>
            <div>
              <label style={LBL}>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} style={INP}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={LBL}>Product Name *</label>
            <input type="text" value={form.name} onChange={e => set('name', e.target.value)} required style={INP} placeholder="Enter product name" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={LBL}>Quantity</label>
              <input type="number" min="0" step="1" value={form.quantity} onChange={e => set('quantity', parseFloat(e.target.value) || 0)} style={INP} />
            </div>
            <div>
              <label style={LBL}>Min Qty (Low Stock Alert)</label>
              <input type="number" min="0" step="1" value={form.minQuantity} onChange={e => set('minQuantity', parseFloat(e.target.value) || 0)} style={INP} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={LBL}>Cost Price ($)</label>
              <input type="number" min="0" step="0.01" value={form.costPrice} onChange={e => set('costPrice', parseFloat(e.target.value) || 0)} style={INP} />
            </div>
            <div>
              <label style={LBL}>Selling Price ($)</label>
              <input type="number" min="0" step="0.01" value={form.sellingPrice} onChange={e => set('sellingPrice', parseFloat(e.target.value) || 0)} style={INP} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={LBL}>Supplier</label>
              {suppliers && suppliers.length > 0 ? (
                <select value={form.supplier} onChange={e => set('supplier', e.target.value)} style={INP}>
                  <option value="">— Select Supplier —</option>
                  {suppliers.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
              ) : (
                <input type="text" value={form.supplier} onChange={e => set('supplier', e.target.value)} style={INP} placeholder="Supplier name" />
              )}
            </div>
            <div>
              <label style={LBL}>Location / Bin</label>
              <input type="text" value={form.location} onChange={e => set('location', e.target.value)} style={INP} placeholder="e.g. A3, Shelf 2" />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={LBL}>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} style={{ ...INP, resize: 'vertical' }} placeholder="Additional notes…" />
          </div>

          {/* Margin preview */}
          {form.costPrice > 0 && form.sellingPrice > 0 && (
            <div style={{ background: '#F0FDF4', borderRadius: 12, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#15803D', fontWeight: 500 }}>
              Margin: <b>{(((form.sellingPrice - form.costPrice) / form.sellingPrice) * 100).toFixed(1)}%</b>
              &nbsp;·&nbsp;Markup: <b>{(((form.sellingPrice - form.costPrice) / form.costPrice) * 100).toFixed(1)}%</b>
              &nbsp;·&nbsp;Profit/unit: <b>${(form.sellingPrice - form.costPrice).toFixed(2)}</b>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '11px 22px', border: 'none', borderRadius: 12, background: '#F2F2F7', color: '#1C1C1E', cursor: 'pointer', fontSize: 14, fontWeight: 500, fontFamily: FONT }}>
              Cancel
            </button>
            <button type="submit" disabled={saving} style={{ padding: '11px 26px', border: 'none', borderRadius: 12, background: '#84CC16', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? 0.8 : 1, fontFamily: FONT }}>
              {saving ? 'Saving…' : (item ? 'Update Item' : 'Add Item')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
